import { useLayoutEffect, useMemo, useRef, useState, VoidFunctionComponent } from 'react';
import { ThreeEvent, useFrame } from '@react-three/fiber';
import { AlwaysDepth, Group, NormalBlending, Object3D } from 'three';
import { OnlyBotThree } from '@animavirtuality/onlybots-three';
import { applyMaterial, DEFAULT_COLOR } from '@/lib/materials';
import { useVoxelExplosion } from '@/hooks/useVoxelExplosion';
import { FeaturedBot, BotInstancedMesh } from '@/lib/types';
import { deviceProperties } from '@/lib/deviceProperties';
import switchBot1Sound from '@/sounds/ob-sfx-title-switch-bot-1.mp3';
import switchBot2Sound from '@/sounds/ob-sfx-title-switch-bot-2.mp3';
import switchBot3Sound from '@/sounds/ob-sfx-title-switch-bot-3.mp3';
import switchBot4Sound from '@/sounds/ob-sfx-title-switch-bot-4.mp3';
import { useOnlybotsSound } from '@/hooks/useOnlybotsSound';
import { SelectBloom } from '@/components/SelectBloom';
import { ThreeBotRenderer } from '@/components/BotRenderer';
import { useRouterContext } from '@/context/RouterContext';

const threeGroup = new Group();
const TRANSFORMATION_STORAGE = new Object3D();
const EXPLODE_TIME = 0.3;
const SWIRL_TIME = 0.5;
const CONSOLIDATE_TIME = 0.5;
const ANIMATION_TIME = EXPLODE_TIME + SWIRL_TIME + CONSOLIDATE_TIME;
const STATIC_TIME = 4;

interface Props {
    featuredBots: FeaturedBot[];
}

export const CycleOnlyBot: VoidFunctionComponent<Props> = ({ featuredBots }) => {
    const { isTouchDevice } = deviceProperties();
    const [hovered, setHovered] = useState<boolean>(false);
    const { navigate } = useRouterContext();
    const [botIndex, setBotIndex] = useState<number>(0);
    const timer = useRef<number>(0);
    const firstPass = useRef<boolean>(true);
    const currentFeaturedBot: FeaturedBot | undefined = featuredBots[botIndex];
    const id = currentFeaturedBot?.id;
    const featuredAdjustments = currentFeaturedBot.featuredAdjustments ?? {
        scale: 1,
        y: 0,
    };
    const materials = currentFeaturedBot?.materials ?? [];
    const onlyBotThree: OnlyBotThree | undefined = currentFeaturedBot?.onlyBotThree;

    const [playSwitchBot1Sound] = useOnlybotsSound(switchBot1Sound);
    const [playSwitchBot2Sound] = useOnlybotsSound(switchBot2Sound);
    const [playSwitchBot3Sound] = useOnlybotsSound(switchBot3Sound);
    const [playSwitchBot4Sound] = useOnlybotsSound(switchBot4Sound);
    const switchBotSounds = useMemo(
        () => [playSwitchBot1Sound, playSwitchBot2Sound, playSwitchBot3Sound, playSwitchBot4Sound],
        [playSwitchBot1Sound, playSwitchBot2Sound, playSwitchBot3Sound, playSwitchBot4Sound]
    );

    // one instance ref per possible material
    const instance0 = useRef<BotInstancedMesh>(null);
    const instance1 = useRef<BotInstancedMesh>(null);
    const instance2 = useRef<BotInstancedMesh>(null);
    const instance3 = useRef<BotInstancedMesh>(null);
    const instancedMeshRefs = useMemo(() => [instance0, instance1, instance2, instance3], []);
    const maxVoxelLengths = useMemo(
        () =>
            Array.from({ length: 4 }, (_, i) => {
                return featuredBots.reduce((max, { materials }) => {
                    return Math.max(max, materials?.[i]?.voxels?.length ?? 0);
                }, 0);
            }),
        [featuredBots]
    );

    const groupRef = useRef<Group>(threeGroup);
    const { animationState, cycleBot } = useVoxelExplosion(
        instancedMeshRefs,
        materials ?? [],
        groupRef,
        EXPLODE_TIME,
        SWIRL_TIME,
        CONSOLIDATE_TIME
    );

    useLayoutEffect(() => {
        instancedMeshRefs.forEach((ref, i) => {
            const mesh = ref.current;
            if (!mesh) {
                return;
            }

            const material = materials?.[i];
            if (!material) {
                mesh.count = 0;
                return;
            }
            mesh.count = material.voxels.length;

            applyMaterial(mesh, material.color, material.preset);
            material.voxels.forEach(({ position: [x, y, z] }, index) => {
                TRANSFORMATION_STORAGE.position.set(x, y, z);
                TRANSFORMATION_STORAGE.scale.setScalar(1);
                TRANSFORMATION_STORAGE.updateMatrix();

                mesh.setMatrixAt(index, TRANSFORMATION_STORAGE.matrix);
                mesh.instanceMatrix.needsUpdate = true;
            });
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const over = (e: ThreeEvent<PointerEvent>): void => {
        e.stopPropagation();
        setHovered(true);
    };
    const out = (): void => {
        setHovered(false);
    };
    useLayoutEffect(() => {
        if (hovered) {
            document.body.style.cursor = 'pointer';
        }
        return () => {
            document.body.style.cursor = 'auto';
        };
    }, [hovered]);

    // cycle the bot, if it is our first time, wait for STATIC_TIME to pass before cycling
    // for subsequent times, wait for
    useFrame((_, delta) => {
        if (featuredBots.length > 1) {
            timer.current += delta;
            if (timer.current > (firstPass.current ? STATIC_TIME : STATIC_TIME + ANIMATION_TIME)) {
                timer.current = 0;
                firstPass.current = false;
                const nextBotIndex = botIndex + 1 < featuredBots.length ? botIndex + 1 : 0;
                cycleBot(
                    featuredBots[nextBotIndex].materials ?? [],
                    featuredBots[nextBotIndex].anchor ?? { x: 0, y: 0, z: 0 },
                    () => setBotIndex(nextBotIndex)
                );
                const soundIndex = nextBotIndex % 4;
                const sound = switchBotSounds[soundIndex];
                sound();
            }
        }
    });

    return (
        <>
            {!isTouchDevice &&
                featuredBots.map(({ qrTexture }, i) =>
                    !id || qrTexture === undefined ? null : (
                        <group key={i}>
                            <sprite position={[15, 7, 11]} scale={botIndex === i ? 10 : 0}>
                                <spriteMaterial
                                    map={qrTexture}
                                    color={0x101010}
                                    alphaTest={0.01}
                                    transparent={true}
                                    blending={NormalBlending}
                                    clipIntersection={true}
                                    colorWrite={true}
                                    depthFunc={AlwaysDepth}
                                    depthWrite={false}
                                    opacity={1.0}
                                    precision={'highp'}
                                    toneMapped={false}
                                />
                            </sprite>
                        </group>
                    )
                )}
            {onlyBotThree && (
                <group scale={featuredAdjustments.scale} position={[0, featuredAdjustments.y, 0]}>
                    <group
                        visible={animationState === 'STATIC'}
                        onPointerOver={over}
                        onPointerOut={out}
                        onClick={(e: ThreeEvent<MouseEvent>) => {
                            e.stopPropagation();
                            if (id) {
                                navigate(id.toString());
                            }
                        }}
                    >
                        <ThreeBotRenderer bot={onlyBotThree} />
                    </group>
                    <group ref={groupRef} visible={animationState !== 'STATIC'}>
                        {instancedMeshRefs.map((ref, i) => (
                            <SelectBloom key={i} enabled>
                                <instancedMesh ref={ref} args={[undefined, undefined, maxVoxelLengths[i]]}>
                                    <boxGeometry />
                                    <meshPhysicalMaterial color={DEFAULT_COLOR} />
                                </instancedMesh>
                            </SelectBloom>
                        ))}
                    </group>
                </group>
            )}
        </>
    );
};
